import { PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from 'react'
import { Plane, Vector2, Vector3 } from 'three'
import { useDocumentListener } from '@modugen/ui-toolkit'
import { useThree } from '@react-three/fiber'
import useStoreyImageOriginStore from './store'

/** Rotation snaps to multiples of this angle (90°). */
const ROTATION_SNAP_RAD = Math.PI / 2

/** Magnetic drag-snap tolerance around a 90° multiple (~8°). */
const ROTATION_SNAP_TOLERANCE_RAD = (8 * Math.PI) / 180

interface RotateDragContext {
  plane: Plane
  /** The gizmo origin — the rotation pivot. */
  center: Vector3
  /** Pointer angle around `center` at drag start. */
  startAngle: number
  /** The gizmo's rotation at drag start. */
  baseRotation: number
  /** Whether the rotation magnetically snaps near 90° multiples. */
  snapRotation: boolean
}

interface OriginRotateDrag {
  /**
   * Begin rotating the gizmo from a HUD button pointer event. `snapRotation` enables magnetic
   * snapping near 90° multiples; defaults to true.
   */
  startRotateDrag: (event: ReactPointerEvent, snapRotation?: boolean) => void
  /** Whether a rotate drag is in progress (drives the HUD button's active style). */
  isRotating: boolean
}

/**
 * Drag-to-rotate behaviour for the origin gizmo, mirroring the IFC alignment rotate drag: a HUD
 * button's pointer-down starts the drag, document pointer move/up track it, and the rotation is
 * written straight to the origin store. While snapping is enabled the gizmo's absolute
 * orientation magnetically locks to 90° multiples (0/90/180/270°) in world space.
 */
const useOriginRotateDrag = (): OriginRotateDrag => {
  const camera = useThree(state => state.camera)
  const gl = useThree(state => state.gl)
  const raycaster = useThree(state => state.raycaster)

  const [isRotating, setIsRotating] = useState(false)
  const dragRef = useRef<RotateDragContext | null>(null)

  const getWorldOnPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect()
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      )
      raycaster.setFromCamera(ndc, camera)
      const target = new Vector3()
      return raycaster.ray.intersectPlane(plane, target) ? target.clone() : null
    },
    [camera, gl, raycaster],
  )

  useDocumentListener(
    'pointermove',
    (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      const world = getWorldOnPlane(event.clientX, event.clientY, drag.plane)
      if (!world) return

      const angle = Math.atan2(world.y - drag.center.y, world.x - drag.center.x)
      const rawRotation = drag.baseRotation + (angle - drag.startAngle)
      const nearest = Math.round(rawRotation / ROTATION_SNAP_RAD) * ROTATION_SNAP_RAD
      const rotation =
        drag.snapRotation && Math.abs(rawRotation - nearest) <= ROTATION_SNAP_TOLERANCE_RAD
          ? nearest
          : rawRotation
      useStoreyImageOriginStore.getState().setRotation(rotation)
    },
    [getWorldOnPlane],
    isRotating,
  )

  useDocumentListener(
    'pointerup',
    () => {
      dragRef.current = null
      setIsRotating(false)
    },
    [],
    isRotating,
  )

  const startRotateDrag = useCallback(
    (event: ReactPointerEvent, snapRotation = true) => {
      event.stopPropagation()

      const { origin, rotation } = useStoreyImageOriginStore.getState()
      if (!origin) return

      const center = new Vector3(origin.x, origin.y, origin.z)
      const plane = new Plane(new Vector3(0, 0, 1), -center.z)
      const world = getWorldOnPlane(event.clientX, event.clientY, plane)
      const startAngle = world ? Math.atan2(world.y - center.y, world.x - center.x) : 0

      dragRef.current = { plane, center, startAngle, baseRotation: rotation, snapRotation }
      setIsRotating(true)
    },
    [getWorldOnPlane],
  )

  return { startRotateDrag, isRotating }
}

export default useOriginRotateDrag
